import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, FormControl, InputLabel, Select, MenuItem, IconButton,
  CircularProgress, Alert, Paper, Chip, Button, Menu,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { ChevronLeft, ChevronRight, PictureAsPdf, ArrowDropDown } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import type { Dayjs } from 'dayjs';
import apiClient from '../api/client';
import TimesheetGrid from '../components/timesheet/TimesheetGrid';
import generateTimesheetPDF from '../utils/generateTimesheetPDF';
import { formatHoursMinutes } from '../utils/dateUtils';
import { Timesheet, TimesheetEntry } from '../types';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

function getStatusChip(status: string) {
  switch (status) {
    case 'approved': return <Chip label="Approved" size="small" sx={{ backgroundColor: '#D1FAE5', color: '#065F46', fontWeight: 700, fontSize: '0.7rem' }} />;
    case 'submitted': return <Chip label="Submitted" size="small" sx={{ backgroundColor: '#DBEAFE', color: '#1E40AF', fontWeight: 700, fontSize: '0.7rem' }} />;
    case 'rejected':  return <Chip label="Rejected"  size="small" sx={{ backgroundColor: '#FEE2E2', color: '#991B1B', fontWeight: 700, fontSize: '0.7rem' }} />;
    default:          return <Chip label="Billable"  size="small" sx={{ backgroundColor: '#D1FAE5', color: '#065F46', fontWeight: 700, fontSize: '0.7rem' }} />;
  }
}

export default function CustomerTimesheetsPage() {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [timesheets, setTimesheets] = useState<Timesheet[]>([]);
  const [selectedTsId, setSelectedTsId] = useState<string>('');
  const [entries, setEntries] = useState<TimesheetEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingEntries, setLoadingEntries] = useState(false);

  // Download menu
  const [downloadAnchor, setDownloadAnchor] = useState<null | HTMLElement>(null);

  // Custom dates dialog
  const [customDateOpen, setCustomDateOpen] = useState(false);
  const [fromDate, setFromDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(null);
  const [customLoading, setCustomLoading] = useState(false);
  const [customError, setCustomError] = useState<string | null>(null);

  const loadTimesheets = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get('/timesheets', { params: { month, year } });
      setTimesheets(data);
      setSelectedTsId(data.length > 0 ? data[0].id : '');
      if (data.length === 0) setEntries([]);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [month, year]);

  useEffect(() => { loadTimesheets(); }, [loadTimesheets]);

  // Set page title to client name for customer portal
  useEffect(() => {
    const clientName = timesheets[0]?.client_name;
    document.title = clientName ? `${clientName} — Kovil Timesheets` : 'Kovil Timesheets';
    return () => { document.title = 'Kovil Timesheets'; };
  }, [timesheets]);

  useEffect(() => {
    if (!selectedTsId) return;
    setLoadingEntries(true);
    apiClient.get(`/timesheets/${selectedTsId}/entries`)
      .then(({ data }) => setEntries(data))
      .catch(console.error)
      .finally(() => setLoadingEntries(false));
  }, [selectedTsId]);

  const selectedTs = timesheets.find(t => t.id === selectedTsId);

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  const handleDownloadFullMonth = () => {
    if (!selectedTs) return;
    setDownloadAnchor(null);
    generateTimesheetPDF({
      employeeName: selectedTs.employee_name,
      clientName: selectedTs.client_name,
      jobTitle: selectedTs.job_title,
      month: selectedTs.month,
      year: selectedTs.year,
      status: selectedTs.status,
      expectedDailyHours: selectedTs.expected_daily_hours || 8,
      totalHours: Number(selectedTs.total_hours || 0),
      entries,
      isIndirect: selectedTs.is_indirect,
    });
  };

  const handleOpenCustomDates = () => {
    setDownloadAnchor(null);
    setFromDate(null);
    setToDate(null);
    setCustomError(null);
    setCustomDateOpen(true);
  };

  const handleCustomDownload = async () => {
    if (!selectedTs || !fromDate || !toDate) return;
    setCustomLoading(true);
    setCustomError(null);
    try {
      const fromStr = fromDate.format('YYYY-MM-DD');
      const toStr = toDate.format('YYYY-MM-DD');
      const { data } = await apiClient.get('/reports/date-range-entries', {
        params: { employee_id: selectedTs.employee_id, from: fromStr, to: toStr },
      });
      if (!data.entries || data.entries.length === 0) {
        setCustomError('No timesheet data found for the selected dates.');
        return;
      }
      generateTimesheetPDF({
        employeeName: selectedTs.employee_name,
        clientName: selectedTs.client_name,
        jobTitle: selectedTs.job_title,
        month: selectedTs.month,
        year: selectedTs.year,
        status: selectedTs.status,
        expectedDailyHours: selectedTs.expected_daily_hours || 8,
        totalHours: data.entries.reduce((s: number, e: any) => s + Number(e.total_hours || 0), 0),
        entries: data.entries,
        dateRange: { from: fromStr, to: toStr },
        isIndirect: selectedTs.is_indirect,
      });
      setCustomDateOpen(false);
    } catch (err: any) {
      setCustomError(err.response?.data?.error || 'Failed to fetch entries for the selected dates.');
    } finally {
      setCustomLoading(false);
    }
  };

  const dateRangeValid = fromDate && toDate && toDate.isAfter(fromDate.subtract(1, 'day'));

  return (
    <Box>
      {/* Controls bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid #E2E8F0', borderRadius: '10px', px: 0.5 }}>
          <IconButton size="small" onClick={prevMonth}><ChevronLeft /></IconButton>
          <Typography fontWeight={600} sx={{ minWidth: 130, textAlign: 'center', fontSize: '0.9375rem' }}>
            {MONTHS[month - 1]} {year}
          </Typography>
          <IconButton size="small" onClick={nextMonth}><ChevronRight /></IconButton>
        </Box>

        {timesheets.length > 1 && (
          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel>Employee</InputLabel>
            <Select value={selectedTsId} label="Employee" onChange={(e) => setSelectedTsId(e.target.value)}>
              {timesheets.map(ts => (
                <MenuItem key={ts.id} value={ts.id}>{ts.employee_name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Download button — top right */}
        {selectedTs && (
          <Box sx={{ ml: 'auto' }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<PictureAsPdf />}
              endIcon={<ArrowDropDown />}
              onClick={(e) => setDownloadAnchor(e.currentTarget)}
              disabled={loadingEntries || entries.length === 0}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}
            >
              Download Timesheet
            </Button>
            <Menu
              anchorEl={downloadAnchor}
              open={Boolean(downloadAnchor)}
              onClose={() => setDownloadAnchor(null)}
            >
              <MenuItem onClick={handleDownloadFullMonth}>Full Month</MenuItem>
              <MenuItem onClick={handleOpenCustomDates}>Custom Dates</MenuItem>
            </Menu>
          </Box>
        )}
      </Box>

      {/* Main content */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>
      ) : !selectedTs ? (
        <Alert severity="info">No timesheets found for {MONTHS[month - 1]} {year}.</Alert>
      ) : (
        <Paper
          elevation={2}
          sx={{ borderRadius: '16px', overflow: 'hidden', border: '1px solid #E8EDF2' }}
        >
          {/* Header — matches admin dialog */}
          <Box sx={{ px: 3, pt: 2.5, pb: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
              <Box sx={{ flex: 1 }}>
                <Typography component="span" sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#0F172A' }}>
                  {selectedTs.employee_name}
                </Typography>
                <Typography component="span" sx={{ fontSize: '1.25rem', fontWeight: 400, color: '#64748B' }}>
                  {' '}&mdash; {MONTHS[selectedTs.month - 1]} {selectedTs.year}
                </Typography>
              </Box>
              {getStatusChip(selectedTs.status)}
            </Box>

            {/* Info row */}
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', pb: 2, borderBottom: '1px solid #F1F5F9' }}>
              <Typography variant="body2" color="text.secondary">
                <Box component="span" sx={{ color: '#1E40AF', fontWeight: 600 }}>Employee: </Box>
                {selectedTs.employee_name} ({selectedTs.employee_uid}){selectedTs.job_title ? ` — ${selectedTs.job_title}` : ''}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <Box component="span" sx={{ color: '#1E40AF', fontWeight: 600 }}>Total Hours: </Box>
                {formatHoursMinutes(Number(selectedTs.total_hours || 0))}
              </Typography>
            </Box>
          </Box>

          {/* Grid */}
          <Box>
            {loadingEntries ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
            ) : (
              <TimesheetGrid
                entries={entries}
                editable={false}
                expectedDailyHours={selectedTs.expected_daily_hours || 8}
                onEntryUpdate={() => {}}
                gridHeight="calc(100vh - 310px)"
              />
            )}
          </Box>
        </Paper>
      )}

      {/* Custom Dates dialog */}
      <Dialog open={customDateOpen} onClose={() => setCustomDateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>Download — Custom Dates</DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <DatePicker
                label="From"
                value={fromDate}
                onChange={setFromDate}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
              <DatePicker
                label="To"
                value={toDate}
                onChange={setToDate}
                minDate={fromDate || undefined}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
              {customError && (
                <Alert severity="error" sx={{ py: 0 }}>{customError}</Alert>
              )}
            </Box>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCustomDateOpen(false)} color="inherit">Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCustomDownload}
            disabled={!dateRangeValid || customLoading}
            startIcon={customLoading ? <CircularProgress size={16} /> : <PictureAsPdf />}
          >
            Download PDF
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
